export type GasMode = 'boyle' | 'charles' | 'ideal';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const GAS_LAWS_TOOL_IDENTITY = {
  id: 'gas-laws',
  toolKey: 'chemistry-gas-laws',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

/**
 * Default mode by learner Year (study-chemistry-tools-prd.md §8.1 CT-MODE). Year 11 starts with
 * Boyle's law; Year 12 starts with the combined ideal gas equation. Charles's law is always one
 * click away and is never a Year default.
 */
export const GAS_MODE_YEARS: readonly { mode: GasMode; fromYear: number }[] = [
  { mode: 'boyle', fromYear: 11 },
  { mode: 'ideal', fromYear: 12 },
];

export const GAS_MODES: readonly GasMode[] = ['boyle', 'charles', 'ideal'];

export type PressureUnit = 'kPa' | 'Pa' | 'atm';
export type VolumeUnit = 'L' | 'mL' | 'm3';
export type TemperatureUnit = 'K' | 'C';
export type AmountUnit = 'mol';
export type GasUnit = PressureUnit | VolumeUnit | TemperatureUnit | AmountUnit;
export type GasVariable = 'p' | 'V' | 'n' | 'T';
export type GasSlot = 'p1' | 'V1' | 'T1' | 'p2' | 'V2' | 'T2' | 'p' | 'V' | 'n' | 'T';

export interface GasQuantityInput {
  value: number;
  unit: GasUnit;
}

export interface GasSource {
  label: string;
  detail: string;
}

export const GAS_SOURCES: GasSource[] = [
  {
    label: 'CODATA 2018 via NIST',
    detail: 'Molar gas constant R = 8.314 462 618 J K⁻¹ mol⁻¹, used here as 8.314.',
  },
  {
    label: 'IUPAC Gold Book',
    detail: 'Standard pressure 100 kPa (1 bar); 0 °C = 273.15 K; 1 atm = 101.325 kPa.',
  },
  {
    label: 'Senior chemistry data-sheet values',
    detail:
      'Molar volume 24.8 L mol⁻¹ at SLC (25 °C, 100 kPa) and 22.7 L mol⁻¹ at STP (0 °C, 100 kPa).',
  },
];

export const GAS_REVIEW_STATUS = 'pending-teaching-review' as const;

export const PA_PER_KPA = 1000;
export const KPA_PER_ATM = 101.325;
export const ML_PER_L = 1000;
export const L_PER_M3 = 1000;
export const KELVIN_OFFSET = 273.15;

/** R in J K⁻¹ mol⁻¹, numerically equal to kPa L K⁻¹ mol⁻¹. */
export const GAS_CONSTANT = 8.314;

export type MolarVolumeCondition = 'SLC' | 'STP';

export interface MolarVolumeData {
  condition: MolarVolumeCondition;
  label: string;
  temperatureC: number;
  temperatureK: number;
  pressureKPa: number;
  litresPerMol: number;
  note: string;
}

export const MOLAR_VOLUMES: Record<MolarVolumeCondition, MolarVolumeData> = {
  SLC: {
    condition: 'SLC',
    label: 'SLC — standard laboratory conditions (25 °C, 100 kPa)',
    temperatureC: 25,
    temperatureK: 298.15,
    pressureKPa: 100,
    litresPerMol: 24.8,
    note: 'Room-temperature reference used in Australian senior data sheets.',
  },
  STP: {
    condition: 'STP',
    label: 'STP — standard temperature and pressure (0 °C, 100 kPa)',
    temperatureC: 0,
    temperatureK: 273.15,
    pressureKPa: 100,
    litresPerMol: 22.7,
    note: 'Some older sources use 101.3 kPa (1 atm) for STP, which gives 22.4 L mol⁻¹.',
  },
};

export const LEGACY_STP_MOLAR_VOLUME = { pressureKPa: 101.3, litresPerMol: 22.4 } as const;

export const UNIT_LABELS: Record<GasUnit, string> = {
  kPa: 'kPa',
  Pa: 'Pa',
  atm: 'atm',
  L: 'L',
  mL: 'mL',
  m3: 'm³',
  K: 'K',
  C: '°C',
  mol: 'mol',
};

export const VARIABLE_UNITS: Record<GasVariable, readonly GasUnit[]> = {
  p: ['kPa', 'Pa', 'atm'],
  V: ['L', 'mL', 'm3'],
  n: ['mol'],
  T: ['K', 'C'],
};

const BASE_UNITS: Record<GasVariable, GasUnit> = { p: 'kPa', V: 'L', n: 'mol', T: 'K' };

export const VARIABLE_NAMES: Record<GasVariable, string> = {
  p: 'pressure',
  V: 'volume',
  n: 'amount of gas',
  T: 'temperature',
};

export const SLOT_LABELS: Record<GasSlot, string> = {
  p1: 'p₁',
  V1: 'V₁',
  T1: 'T₁',
  p2: 'p₂',
  V2: 'V₂',
  T2: 'T₂',
  p: 'p',
  V: 'V',
  n: 'n',
  T: 'T',
};

export function slotVariable(slot: GasSlot): GasVariable {
  return slot[0] as GasVariable;
}

const MODE_SLOTS: Record<GasMode, readonly GasSlot[]> = {
  boyle: ['p1', 'V1', 'p2', 'V2'],
  charles: ['V1', 'T1', 'V2', 'T2'],
  ideal: ['p', 'V', 'n', 'T'],
};

export const MODE_UNKNOWNS: Record<GasMode, readonly GasSlot[]> = {
  boyle: ['p2', 'V2'],
  charles: ['V2', 'T2'],
  ideal: ['p', 'V', 'n', 'T'],
};

export type GasTreatment =
  | 'correct'
  | 'celsius-as-kelvin'
  | 'unconverted-units'
  | 'inverted-relationship'
  | 'answer-in-base-unit';

export interface GasSolveRequest {
  mode: GasMode;
  unknown: GasSlot;
  givens: Partial<Record<GasSlot, GasQuantityInput>>;
  answerUnit: GasUnit;
}

export interface GasProblem extends GasSolveRequest {
  id: string;
  title: string;
  context: string;
  /** Reviewed answer in `answerUnit`; tests confirm the model reproduces it. */
  expected: number;
}

export interface GasCalculation {
  value: number | null;
  error: string | null;
}

function toBase(input: GasQuantityInput, treatment: GasTreatment): number {
  const raw = input.value;
  const keepRaw = treatment === 'unconverted-units';
  switch (input.unit) {
    case 'Pa':
      return keepRaw ? raw : raw / PA_PER_KPA;
    case 'atm':
      return keepRaw ? raw : raw * KPA_PER_ATM;
    case 'mL':
      return keepRaw ? raw : raw / ML_PER_L;
    case 'm3':
      return keepRaw ? raw : raw * L_PER_M3;
    case 'C':
      return treatment === 'celsius-as-kelvin' ? raw : raw + KELVIN_OFFSET;
    default:
      return raw;
  }
}

/** Converts a value in the base unit (kPa, L, mol, K) into the requested unit. */
export function fromBase(value: number, unit: GasUnit): number {
  switch (unit) {
    case 'Pa':
      return value * PA_PER_KPA;
    case 'atm':
      return value / KPA_PER_ATM;
    case 'mL':
      return value * ML_PER_L;
    case 'm3':
      return value / L_PER_M3;
    case 'C':
      return value - KELVIN_OFFSET;
    default:
      return value;
  }
}

/** Converts a quantity to kPa, L, mol or K. Exposed for the explorer and the tests. */
export function toBaseUnit(input: GasQuantityInput): number {
  return toBase(input, 'correct');
}

function validateBase(slot: GasSlot, value: number): string | null {
  const variable = slotVariable(slot);
  const label = SLOT_LABELS[slot];
  if (variable === 'T') {
    return value > 0
      ? null
      : `${label} must be above absolute zero (0 K = −273.15 °C). No gas can be at or below 0 K.`;
  }
  return value > 0
    ? null
    : `${label} must be greater than zero. A gas cannot have a zero or negative ${VARIABLE_NAMES[variable]}.`;
}

function solveBase(
  mode: GasMode,
  unknown: GasSlot,
  b: Partial<Record<GasSlot, number>>,
  inverted: boolean,
): number | null {
  const v = (slot: GasSlot) => b[slot] as number;
  if (mode === 'boyle') {
    if (unknown === 'p2')
      return inverted ? (v('p1') * v('V2')) / v('V1') : (v('p1') * v('V1')) / v('V2');
    if (unknown === 'V2')
      return inverted ? (v('V1') * v('p2')) / v('p1') : (v('p1') * v('V1')) / v('p2');
    return null;
  }
  if (mode === 'charles') {
    if (unknown === 'V2')
      return inverted ? (v('V1') * v('T1')) / v('T2') : (v('V1') * v('T2')) / v('T1');
    if (unknown === 'T2')
      return inverted ? (v('T1') * v('V1')) / v('V2') : (v('T1') * v('V2')) / v('V1');
    return null;
  }
  if (inverted) return null;
  switch (unknown) {
    case 'p':
      return (v('n') * GAS_CONSTANT * v('T')) / v('V');
    case 'V':
      return (v('n') * GAS_CONSTANT * v('T')) / v('p');
    case 'n':
      return (v('p') * v('V')) / (GAS_CONSTANT * v('T'));
    case 'T':
      return (v('p') * v('V')) / (v('n') * GAS_CONSTANT);
    default:
      return null;
  }
}

/**
 * Solves one gas-law request. `correct` fails closed on missing, non-numeric, mismatched-unit or
 * non-physical input. The other treatments reproduce common learner errors for feedback only.
 */
export function solveGas(
  request: GasSolveRequest,
  treatment: GasTreatment = 'correct',
): GasCalculation {
  const { mode, unknown, givens, answerUnit } = request;
  if (!MODE_UNKNOWNS[mode].includes(unknown)) {
    return { value: null, error: 'Choose an unknown that belongs to this gas law.' };
  }
  if (!VARIABLE_UNITS[slotVariable(unknown)].includes(answerUnit)) {
    return {
      value: null,
      error: `Choose a ${VARIABLE_NAMES[slotVariable(unknown)]} unit for the answer.`,
    };
  }
  const base: Partial<Record<GasSlot, number>> = {};
  for (const slot of MODE_SLOTS[mode]) {
    if (slot === unknown) continue;
    const given = givens[slot];
    if (!given) return { value: null, error: `Enter a value for ${SLOT_LABELS[slot]}.` };
    if (!Number.isFinite(given.value)) {
      return { value: null, error: `Enter a number for ${SLOT_LABELS[slot]}.` };
    }
    const variable = slotVariable(slot);
    if (!VARIABLE_UNITS[variable].includes(given.unit)) {
      return {
        value: null,
        error: `${SLOT_LABELS[slot]} needs a ${VARIABLE_NAMES[variable]} unit.`,
      };
    }
    const converted = toBase(given, treatment);
    if (treatment === 'correct') {
      const error = validateBase(slot, converted);
      if (error) return { value: null, error };
    }
    base[slot] = converted;
  }
  const solved = solveBase(mode, unknown, base, treatment === 'inverted-relationship');
  if (solved === null || !Number.isFinite(solved)) {
    return { value: null, error: 'This combination cannot be solved with the chosen law.' };
  }
  const value = treatment === 'answer-in-base-unit' ? solved : fromBase(solved, answerUnit);
  return { value, error: null };
}

export function formatSig(value: number, significantFigures = 4): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const rounded = Number(value.toPrecision(significantFigures));
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 6 }).format(rounded);
}

export function formatQuantity(input: GasQuantityInput): string {
  return `${formatSig(input.value)} ${UNIT_LABELS[input.unit]}`;
}

const REARRANGED: Record<GasMode, Partial<Record<GasSlot, string>>> = {
  boyle: { p2: 'p₂ = p₁V₁ ÷ V₂', V2: 'V₂ = p₁V₁ ÷ p₂' },
  charles: { V2: 'V₂ = V₁T₂ ÷ T₁', T2: 'T₂ = T₁V₂ ÷ V₁' },
  ideal: { p: 'p = nRT ÷ V', V: 'V = nRT ÷ p', n: 'n = pV ÷ RT', T: 'T = pV ÷ nR' },
};

function conversionLine(slot: GasSlot, input: GasQuantityInput): string | null {
  const variable = slotVariable(slot);
  const base = toBase(input, 'correct');
  const label = SLOT_LABELS[slot];
  const baseUnit = UNIT_LABELS[BASE_UNITS[variable]];
  switch (input.unit) {
    case 'C':
      return `${label} = ${formatSig(input.value)} °C + 273.15 = ${formatSig(base, 5)} K`;
    case 'Pa':
      return `${label} = ${formatSig(input.value)} Pa ÷ 1000 = ${formatSig(base)} ${baseUnit}`;
    case 'atm':
      return `${label} = ${formatSig(input.value)} atm × 101.325 = ${formatSig(base)} ${baseUnit}`;
    case 'mL':
      return `${label} = ${formatSig(input.value)} mL ÷ 1000 = ${formatSig(base)} ${baseUnit}`;
    case 'm3':
      return `${label} = ${formatSig(input.value)} m³ × 1000 = ${formatSig(base)} ${baseUnit}`;
    default:
      return null;
  }
}

/** Step-by-step working generated from the request, never typed by hand. */
export function gasWorking(request: GasSolveRequest): string[] {
  const lines: string[] = [];
  Object.entries(request.givens).forEach(([slot, input]) => {
    if (!input) return;
    const line = conversionLine(slot as GasSlot, input);
    if (line) lines.push(line);
  });
  if (request.mode === 'ideal')
    lines.push('R = 8.314 kPa L K⁻¹ mol⁻¹ (the same number as 8.314 J K⁻¹ mol⁻¹)');
  lines.push(REARRANGED[request.mode][request.unknown] ?? '');
  const solved = solveGas({
    ...request,
    answerUnit: BASE_UNITS[slotVariable(request.unknown)],
  });
  if (solved.value !== null) {
    const baseUnit = BASE_UNITS[slotVariable(request.unknown)];
    lines.push(
      `${SLOT_LABELS[request.unknown]} = ${formatSig(solved.value)} ${UNIT_LABELS[baseUnit]}`,
    );
    if (request.answerUnit !== baseUnit) {
      lines.push(
        `In ${UNIT_LABELS[request.answerUnit]}: ${formatSig(fromBase(solved.value, request.answerUnit))} ${UNIT_LABELS[request.answerUnit]}`,
      );
    }
  }
  return lines.filter(Boolean);
}

/**
 * Relative tolerance that accepts sensible rounding (3 significant figures) but still separates
 * 22.4 from 22.7 L mol⁻¹.
 */
export const ANSWER_RELATIVE_TOLERANCE = 0.01;

export function withinTolerance(
  answer: number,
  expected: number,
  tolerance = ANSWER_RELATIVE_TOLERANCE,
): boolean {
  if (!Number.isFinite(answer) || !Number.isFinite(expected)) return false;
  if (expected === 0) return Math.abs(answer) < tolerance;
  return Math.abs(answer - expected) <= Math.abs(expected) * tolerance;
}

export type AnswerStatus = 'correct' | 'misconception' | 'incorrect' | 'invalid';

export interface AnswerCheck {
  status: AnswerStatus;
  title: string;
  feedback: string;
}

export interface NumericMisconception {
  value: number;
  feedback: string;
}

/**
 * Shared numeric marking: accept within tolerance, then diagnose a known wrong method, then give
 * direction-specific feedback. Misconceptions that equal the correct value are ignored.
 */
export function checkNumericAnswer(
  answer: number,
  expected: number,
  misconceptions: NumericMisconception[],
  correctFeedback: string,
  unitLabel: string,
  tolerance = ANSWER_RELATIVE_TOLERANCE,
): AnswerCheck {
  if (!Number.isFinite(answer)) {
    return {
      status: 'invalid',
      title: 'Enter a number',
      feedback: 'Type your answer as a number, without units.',
    };
  }
  if (withinTolerance(answer, expected, tolerance)) {
    return { status: 'correct', title: 'Correct', feedback: correctFeedback };
  }
  const diagnosed = misconceptions.find(
    (item) =>
      Number.isFinite(item.value) &&
      !withinTolerance(item.value, expected, tolerance) &&
      withinTolerance(answer, item.value, tolerance),
  );
  if (diagnosed) {
    return {
      status: 'misconception',
      title: 'Not yet — check your method',
      feedback: diagnosed.feedback,
    };
  }
  const direction = answer > expected ? 'too large' : 'too small';
  return {
    status: 'incorrect',
    title: 'Not yet',
    feedback: `Your answer is ${direction}. The model gives ${formatSig(expected, 3)} ${unitLabel}. Compare each step of the working below with yours.`,
  };
}

function misconceptionFeedback(
  problem: GasSolveRequest,
  treatment: GasTreatment,
  value: number,
): string {
  const impossible =
    value <= 0 ? ' Your method even gives a zero or negative quantity, which no gas can have.' : '';
  switch (treatment) {
    case 'celsius-as-kelvin':
      return `You used the Celsius temperature directly. Gas-law calculations need kelvin: T(K) = T(°C) + 273.15. The Celsius zero is just the freezing point of water, so doubling a °C value does not double the particle energy.${impossible}`;
    case 'unconverted-units':
      return 'Convert to kPa and L before using R = 8.314 kPa L K⁻¹ mol⁻¹: 1 atm = 101.325 kPa, 1 kPa = 1000 Pa, 1 L = 1000 mL and 1 m³ = 1000 L.';
    case 'inverted-relationship':
      return problem.mode === 'boyle'
        ? 'You treated pressure and volume as directly proportional. At constant temperature, a smaller volume gives a higher pressure: p₁V₁ = p₂V₂.'
        : 'You made volume and kelvin temperature change in opposite directions. At constant pressure, V ÷ T stays constant, so heating a gas expands it.';
    case 'answer-in-base-unit':
      return `Your working is right, but the answer is in ${UNIT_LABELS[BASE_UNITS[slotVariable(problem.unknown)]]}. Give it in ${UNIT_LABELS[problem.answerUnit]} as the question asks.`;
    default:
      return '';
  }
}

const DIAGNOSED_TREATMENTS: GasTreatment[] = [
  'celsius-as-kelvin',
  'unconverted-units',
  'inverted-relationship',
  'answer-in-base-unit',
];

const CORRECT_REASONS: Record<GasMode, string> = {
  boyle:
    'At constant temperature and amount, p × V stays the same, so pressure and volume change in opposite directions.',
  charles:
    'At constant pressure and amount, V ÷ T (in kelvin) stays the same, so volume is proportional to kelvin temperature.',
  ideal: 'pV = nRT links all four quantities once every value is in kPa, L, mol and K.',
};

export function checkGasAnswer(problem: GasProblem, answer: number): AnswerCheck {
  const correct = solveGas(problem);
  if (correct.value === null) {
    return {
      status: 'invalid',
      title: 'Problem unavailable',
      feedback: correct.error ?? 'This problem cannot be solved.',
    };
  }
  const misconceptions: NumericMisconception[] = DIAGNOSED_TREATMENTS.flatMap((treatment) => {
    const result = solveGas(problem, treatment);
    return result.value === null
      ? []
      : [
          {
            value: result.value,
            feedback: misconceptionFeedback(problem, treatment, result.value),
          },
        ];
  });
  return checkNumericAnswer(
    answer,
    correct.value,
    misconceptions,
    `${CORRECT_REASONS[problem.mode]} ${SLOT_LABELS[problem.unknown]} = ${formatSig(correct.value, 3)} ${UNIT_LABELS[problem.answerUnit]}.`,
    UNIT_LABELS[problem.answerUnit],
  );
}

const q = (value: number, unit: GasUnit): GasQuantityInput => ({ value, unit });

// prettier-ignore
export const GAS_PROBLEMS: Record<GasMode, GasProblem[]> = {
  boyle: [
    { id: 'boyle-halve-volume', mode: 'boyle', title: 'Halve the volume', context: 'A gas at 100 kPa fills 6.0 L. It is compressed to 3.0 L at the same temperature. Find the new pressure.', givens: { p1: q(100, 'kPa'), V1: q(6, 'L'), V2: q(3, 'L') }, unknown: 'p2', answerUnit: 'kPa', expected: 200 },
    { id: 'boyle-pressure-quadruple', mode: 'boyle', title: 'Four times the pressure', context: 'A 2.0 L sample at 100 kPa is squeezed until the pressure reaches 400 kPa. Find the new volume.', givens: { p1: q(100, 'kPa'), V1: q(2, 'L'), p2: q(400, 'kPa') }, unknown: 'V2', answerUnit: 'L', expected: 0.5 },
    { id: 'boyle-expand', mode: 'boyle', title: 'Let it expand', context: 'A gas at 150 kPa occupies 4.0 L. It expands to 10.0 L at constant temperature. Find the new pressure.', givens: { p1: q(150, 'kPa'), V1: q(4, 'L'), V2: q(10, 'L') }, unknown: 'p2', answerUnit: 'kPa', expected: 60 },
    { id: 'boyle-cylinder-release', mode: 'boyle', title: 'Release from a cylinder', context: 'A 1.2 L cylinder holds gas at 250 kPa. The gas is released to 100 kPa at the same temperature. Find its volume.', givens: { p1: q(250, 'kPa'), V1: q(1.2, 'L'), p2: q(100, 'kPa') }, unknown: 'V2', answerUnit: 'L', expected: 3 },
    { id: 'boyle-syringe', mode: 'boyle', title: 'Sealed syringe', context: 'A sealed syringe holds 50 mL of air at 101.3 kPa. The plunger is pushed in to 20 mL. Find the new pressure.', givens: { p1: q(101.3, 'kPa'), V1: q(50, 'mL'), V2: q(20, 'mL') }, unknown: 'p2', answerUnit: 'kPa', expected: 253.25 },
    { id: 'boyle-diver-balloon', mode: 'boyle', title: 'Balloon under water', context: 'A 5.0 L balloon at the surface (100 kPa) is taken to a depth where the pressure is 200 kPa. Assume constant temperature. Find its volume.', givens: { p1: q(100, 'kPa'), V1: q(5, 'L'), p2: q(200, 'kPa') }, unknown: 'V2', answerUnit: 'L', expected: 2.5 },
    { id: 'boyle-millilitres', mode: 'boyle', title: 'Answer in millilitres', context: 'A gas fills 750 mL at 80 kPa. The pressure rises to 120 kPa at the same temperature. Find the new volume in mL.', givens: { p1: q(80, 'kPa'), V1: q(750, 'mL'), p2: q(120, 'kPa') }, unknown: 'V2', answerUnit: 'mL', expected: 500 },
    { id: 'boyle-pressure-drop', mode: 'boyle', title: 'Bigger container', context: 'A gas at 300 kPa fills 2.4 L. It is moved into a 3.6 L container at the same temperature. Find the new pressure.', givens: { p1: q(300, 'kPa'), V1: q(2.4, 'L'), V2: q(3.6, 'L') }, unknown: 'p2', answerUnit: 'kPa', expected: 200 },
    { id: 'boyle-atmospheres', mode: 'boyle', title: 'Working in atmospheres', context: 'A gas occupies 10 L at 1.0 atm. The pressure rises to 2.5 atm at constant temperature. Find the new volume.', givens: { p1: q(1, 'atm'), V1: q(10, 'L'), p2: q(2.5, 'atm') }, unknown: 'V2', answerUnit: 'L', expected: 4 },
  ],
  charles: [
    { id: 'charles-kelvin-heat', mode: 'charles', title: 'Heat in kelvin', context: 'A 2.00 L gas sample at 300 K is heated to 450 K at constant pressure. Find the new volume.', givens: { V1: q(2, 'L'), T1: q(300, 'K'), T2: q(450, 'K') }, unknown: 'V2', answerUnit: 'L', expected: 3 },
    { id: 'charles-celsius-heat', mode: 'charles', title: 'Heat from 27 °C', context: 'A 1.50 L sample at 27 °C is heated to 127 °C at constant pressure. Find the new volume.', givens: { V1: q(1.5, 'L'), T1: q(27, 'C'), T2: q(127, 'C') }, unknown: 'V2', answerUnit: 'L', expected: 1.99975 },
    { id: 'charles-cool-to-zero', mode: 'charles', title: 'Cool to 0 °C', context: 'A 5.0 L sample at 100 °C is cooled to 0 °C at constant pressure. Find the new volume.', givens: { V1: q(5, 'L'), T1: q(100, 'C'), T2: q(0, 'C') }, unknown: 'V2', answerUnit: 'L', expected: 3.66006 },
    { id: 'charles-millilitres', mode: 'charles', title: 'Warm a flask', context: 'A flask holds 600 mL of air at 20 °C. It is warmed to 80 °C at constant pressure. Find the new volume in mL.', givens: { V1: q(600, 'mL'), T1: q(20, 'C'), T2: q(80, 'C') }, unknown: 'V2', answerUnit: 'mL', expected: 722.804 },
    { id: 'charles-find-temperature', mode: 'charles', title: 'Find the temperature', context: 'A 3.0 L sample is at 250 K. To what temperature must it be heated, at constant pressure, to fill 4.5 L?', givens: { V1: q(3, 'L'), T1: q(250, 'K'), V2: q(4.5, 'L') }, unknown: 'T2', answerUnit: 'K', expected: 375 },
    { id: 'charles-below-zero', mode: 'charles', title: 'Cool below 0 °C', context: 'A 2.5 L sample at 25 °C is cooled to −25 °C at constant pressure. Find the new volume.', givens: { V1: q(2.5, 'L'), T1: q(25, 'C'), T2: q(-25, 'C') }, unknown: 'V2', answerUnit: 'L', expected: 2.08075 },
    { id: 'charles-liquid-nitrogen', mode: 'charles', title: 'Liquid nitrogen balloon', context: 'A 4.0 L balloon at 300 K is cooled to 77 K at constant pressure. Assume it stays a gas. Find the new volume.', givens: { V1: q(4, 'L'), T1: q(300, 'K'), T2: q(77, 'K') }, unknown: 'V2', answerUnit: 'L', expected: 1.02667 },
    { id: 'charles-double-volume', mode: 'charles', title: 'Double the volume', context: 'A 10.0 L sample is at 273 K. At what kelvin temperature will it fill 20.0 L at constant pressure?', givens: { V1: q(10, 'L'), T1: q(273, 'K'), V2: q(20, 'L') }, unknown: 'T2', answerUnit: 'K', expected: 546 },
    { id: 'charles-celsius-doubling-trap', mode: 'charles', title: 'Does 20 → 40 °C double it?', context: 'A 1.00 L sample at 20 °C is heated to 40 °C at constant pressure. Find the new volume.', givens: { V1: q(1, 'L'), T1: q(20, 'C'), T2: q(40, 'C') }, unknown: 'V2', answerUnit: 'L', expected: 1.06822 },
  ],
  ideal: [
    { id: 'ideal-one-mole-slc', mode: 'ideal', title: 'One mole at SLC', context: 'Find the volume of 1.00 mol of an ideal gas at 25 °C and 100 kPa.', givens: { n: q(1, 'mol'), T: q(25, 'C'), p: q(100, 'kPa') }, unknown: 'V', answerUnit: 'L', expected: 24.7882 },
    { id: 'ideal-pressure', mode: 'ideal', title: 'Pressure in a flask', context: '0.500 mol of gas is held in a 10.0 L flask at 300 K. Find the pressure.', givens: { n: q(0.5, 'mol'), T: q(300, 'K'), V: q(10, 'L') }, unknown: 'p', answerUnit: 'kPa', expected: 124.71 },
    { id: 'ideal-moles', mode: 'ideal', title: 'How many moles?', context: 'A 2.00 L container holds gas at 150 kPa and 27 °C. Find the amount of gas.', givens: { p: q(150, 'kPa'), V: q(2, 'L'), T: q(27, 'C') }, unknown: 'n', answerUnit: 'mol', expected: 0.120219 },
    { id: 'ideal-temperature', mode: 'ideal', title: 'Find the temperature', context: '0.250 mol of gas in a 5.00 L vessel has a pressure of 200 kPa. Find the temperature in kelvin.', givens: { n: q(0.25, 'mol'), V: q(5, 'L'), p: q(200, 'kPa') }, unknown: 'T', answerUnit: 'K', expected: 481.116 },
    { id: 'ideal-millilitres', mode: 'ideal', title: 'A 500 mL sample', context: 'A 500 mL sample of gas is at 101.3 kPa and 0 °C. Find the amount of gas.', givens: { p: q(101.3, 'kPa'), V: q(500, 'mL'), T: q(0, 'C') }, unknown: 'n', answerUnit: 'mol', expected: 0.0223033 },
    { id: 'ideal-cubic-metres', mode: 'ideal', title: 'Cubic metres', context: '3.00 mol of gas fills 0.0500 m³ at 400 K. Find the pressure.', givens: { n: q(3, 'mol'), V: q(0.05, 'm3'), T: q(400, 'K') }, unknown: 'p', answerUnit: 'kPa', expected: 199.536 },
    { id: 'ideal-atmospheres', mode: 'ideal', title: 'Pressure in atmospheres', context: '0.100 mol of gas is at 2.00 atm and 37 °C. Find its volume.', givens: { n: q(0.1, 'mol'), p: q(2, 'atm'), T: q(37, 'C') }, unknown: 'V', answerUnit: 'L', expected: 1.27243 },
    { id: 'ideal-hot-vessel', mode: 'ideal', title: 'Hot vessel', context: '1.50 mol of gas in a 20.0 L vessel has a pressure of 250 kPa. Find the temperature in kelvin.', givens: { n: q(1.5, 'mol'), V: q(20, 'L'), p: q(250, 'kPa') }, unknown: 'T', answerUnit: 'K', expected: 400.93 },
    { id: 'ideal-pascals', mode: 'ideal', title: 'Pressure in pascals', context: 'A 2.00 L container holds gas at 120 000 Pa and 300 K. Find the amount of gas.', givens: { p: q(120000, 'Pa'), V: q(2, 'L'), T: q(300, 'K') }, unknown: 'n', answerUnit: 'mol', expected: 0.0962232 },
    { id: 'ideal-small-flask', mode: 'ideal', title: 'Small flask', context: '0.0200 mol of gas is sealed in a 400 mL flask at 25 °C. Find the pressure.', givens: { n: q(0.02, 'mol'), V: q(400, 'mL'), T: q(25, 'C') }, unknown: 'p', answerUnit: 'kPa', expected: 123.941 },
  ],
};

export interface GasPredictionOption {
  id: string;
  label: string;
  correct: boolean;
  feedback: string;
}

export interface GasPrediction {
  mode: GasMode;
  question: string;
  options: GasPredictionOption[];
}

export const GAS_PREDICTIONS: Record<GasMode, GasPrediction> = {
  boyle: {
    mode: 'boyle',
    question: 'Temperature and amount stay fixed. What shape will the pressure–volume graph have?',
    options: [
      {
        id: 'inverse-curve',
        label: 'A curve that falls steeply, then levels off',
        correct: true,
        feedback:
          'Yes. p × V is constant, so halving the volume doubles the pressure. Equal steps in volume give smaller and smaller drops in pressure.',
      },
      {
        id: 'straight-down',
        label: 'A straight line sloping down',
        correct: false,
        feedback:
          'Pressure does fall as volume rises, but not by equal steps. A straight line would reach zero pressure at a finite volume. Because p × V is constant, the graph curves and never touches either axis.',
      },
      {
        id: 'straight-up',
        label: 'A straight line sloping up',
        correct: false,
        feedback:
          'Squeezing a gas into a smaller space makes particles hit the walls more often, so pressure rises as volume falls — not as volume rises.',
      },
      {
        id: 'flat',
        label: 'A flat line — pressure stays the same',
        correct: false,
        feedback:
          'The same particles in a smaller space hit each square centimetre of wall more often, so the pressure changes.',
      },
    ],
  },
  charles: {
    mode: 'charles',
    question:
      'Pressure and amount stay fixed. What will a graph of volume against kelvin temperature look like?',
    options: [
      {
        id: 'origin-line',
        label: 'A straight line through the origin',
        correct: true,
        feedback:
          'Yes. V ÷ T is constant when T is in kelvin, so the line passes through 0 K. On a Celsius axis the same line meets zero volume at −273.15 °C — absolute zero.',
      },
      {
        id: 'zero-celsius',
        label: 'A straight line that reaches zero volume at 0 °C',
        correct: false,
        feedback:
          'At 0 °C a gas still has a large volume (22.7 L for one mole at 100 kPa). Extend the line: volume reaches zero only at −273.15 °C, which is 0 K.',
      },
      {
        id: 'falling-curve',
        label: 'A curve that falls as temperature rises',
        correct: false,
        feedback:
          'That is the shape of Boyle’s law. Heating a gas makes particles move faster, so at constant pressure the gas expands.',
      },
      {
        id: 'flat',
        label: 'A flat line — volume does not depend on temperature',
        correct: false,
        feedback:
          'Faster particles push harder on a movable piston. At constant pressure the gas expands until the pressure balances again.',
      },
    ],
  },
  ideal: {
    mode: 'ideal',
    question:
      'Amount and volume stay fixed. You double the kelvin temperature. What happens to the pressure?',
    options: [
      {
        id: 'doubles',
        label: 'It doubles',
        correct: true,
        feedback:
          'Yes. p = nRT ÷ V, so p is proportional to T. Particles move about 1.41 times faster, so each hit is harder and hits happen more often: 1.41 × 1.41 ≈ 2.',
      },
      {
        id: 'quadruples',
        label: 'It becomes four times larger',
        correct: false,
        feedback:
          'Two effects multiply, but each is only √2 ≈ 1.41: harder hits and more frequent hits. Together they double the pressure, as p = nRT ÷ V predicts.',
      },
      {
        id: 'unchanged',
        label: 'It stays the same',
        correct: false,
        feedback:
          'The particle number and space are fixed, but hotter particles move faster. They hit the walls harder and more often, so the pressure rises.',
      },
      {
        id: 'halves',
        label: 'It halves',
        correct: false,
        feedback:
          'Pressure and temperature change in the same direction when volume is fixed. That is why a sealed can is dangerous to heat.',
      },
    ],
  },
};

export interface ExplorerState {
  nMol: number;
  temperatureK: number;
  volumeL: number;
}

export const EXPLORER_DEFAULTS: Record<GasMode, ExplorerState> = {
  boyle: { nMol: 0.2, temperatureK: 298.15, volumeL: 5 },
  charles: { nMol: 0.2, temperatureK: 300, volumeL: 0 },
  ideal: { nMol: 0.2, temperatureK: 300, volumeL: 5 },
};

/** Constant pressure held by the movable piston in Charles mode. */
export const CHARLES_PRESSURE_KPA = 100;

export const EXPLORER_RANGES = {
  volumeL: { min: 1, max: 10, step: 0.5 },
  temperatureK: { min: 100, max: 600, step: 25 },
  nMol: { min: 0.05, max: 0.5, step: 0.05 },
} as const;

/** Reference state for the relative particle readouts (0.200 mol, 298.15 K, 5.00 L). */
export const PARTICLE_REFERENCE: ExplorerState = { nMol: 0.2, temperatureK: 298.15, volumeL: 5 };
export const PARTICLES_PER_MOL = 60;
export const MIN_PARTICLES = 3;
export const MAX_PARTICLES = 30;

export function idealPressureKPa(nMol: number, temperatureK: number, volumeL: number): number {
  return (nMol * GAS_CONSTANT * temperatureK) / volumeL;
}

export function idealVolumeL(nMol: number, temperatureK: number, pressureKPa: number): number {
  return (nMol * GAS_CONSTANT * temperatureK) / pressureKPa;
}

export interface ParticleModel {
  particleCount: number;
  /** Root-mean-square speed relative to the reference state: √(T ÷ T_ref). */
  relativeSpeed: number;
  /** Wall hits per unit area per second relative to the reference: (n ÷ V)√T. */
  relativeCollisionRate: number;
  pressureKPa: number;
}

export function particleModel(state: ExplorerState): ParticleModel {
  const relativeSpeed = Math.sqrt(state.temperatureK / PARTICLE_REFERENCE.temperatureK);
  const density = state.nMol / state.volumeL;
  const referenceDensity = PARTICLE_REFERENCE.nMol / PARTICLE_REFERENCE.volumeL;
  return {
    particleCount: Math.min(
      MAX_PARTICLES,
      Math.max(MIN_PARTICLES, Math.round(state.nMol * PARTICLES_PER_MOL)),
    ),
    relativeSpeed,
    relativeCollisionRate: (density / referenceDensity) * relativeSpeed,
    pressureKPa: idealPressureKPa(state.nMol, state.temperatureK, state.volumeL),
  };
}

export interface GraphPoint {
  x: number;
  y: number;
}

const GRAPH_POINTS = 37;

export function boyleSeries(
  nMol: number,
  temperatureK: number,
  min: number = EXPLORER_RANGES.volumeL.min,
  max: number = EXPLORER_RANGES.volumeL.max,
): GraphPoint[] {
  return Array.from({ length: GRAPH_POINTS }, (_, index) => {
    const volume = min + ((max - min) * index) / (GRAPH_POINTS - 1);
    return { x: volume, y: idealPressureKPa(nMol, temperatureK, volume) };
  });
}

/** V against T in kelvin, from 0 K so the learner can see the line meet the origin. */
export function charlesSeries(
  nMol: number,
  pressureKPa: number,
  max: number = EXPLORER_RANGES.temperatureK.max,
): GraphPoint[] {
  return Array.from({ length: GRAPH_POINTS }, (_, index) => {
    const temperature = (max * index) / (GRAPH_POINTS - 1);
    return { x: temperature, y: idealVolumeL(nMol, temperature, pressureKPa) };
  });
}

/** p against T in kelvin at fixed n and V. */
export function pressureTemperatureSeries(
  nMol: number,
  volumeL: number,
  max: number = EXPLORER_RANGES.temperatureK.max,
): GraphPoint[] {
  return Array.from({ length: GRAPH_POINTS }, (_, index) => {
    const temperature = (max * index) / (GRAPH_POINTS - 1);
    return { x: temperature, y: idealPressureKPa(nMol, temperature, volumeL) };
  });
}

export const GAS_MODEL_BOUNDARIES = [
  'The model is an ideal gas: particles have no volume and no attractions. Real gases differ at high pressure and low temperature, and they condense before 0 K.',
  'Particle numbers, speeds and box sizes are scaled for display. Readouts are relative to 0.200 mol at 298.15 K in 5.00 L.',
  'This is not a practical guide. Compressed and heated gases need teacher supervision and a risk assessment.',
];
